import { Checkbox } from '@shared/components';
import { AMOUNT_BASIS_LABELS } from '../utils/amountBasis';
import type { AmountBasis } from '../utils/amountBasis';
import type { SelectionUnit } from '../utils/invoiceSelection';
import type { AddOnSelectionRow } from '../utils/selectionTableRows';
import { DueDateLabel } from './DueDateLabel';

interface Props {
    rows: AddOnSelectionRow[];
    emptyMessage: string;
    onToggle: (unit: SelectionUnit) => void;
    locked?: boolean;
    basis?: AmountBasis;
}

const GRID =
    'grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center @min-[28rem]:grid-cols-[2.5rem_minmax(0,1fr)_3.5rem_minmax(6.5rem,auto)_minmax(6.5rem,auto)]';
const QTY_CELL = 'col-start-2 row-start-2 @min-[28rem]:col-start-3 @min-[28rem]:row-start-1';
const DUE_CELL = 'col-start-2 row-start-3 @min-[28rem]:col-start-4 @min-[28rem]:row-start-1';
const NET_CELL = 'col-start-3 row-start-1 pl-2 pr-3 text-right @min-[28rem]:col-start-5';

interface AddOnRowProps {
    row: AddOnSelectionRow;
    locked: boolean;
    basis: AmountBasis;
    onToggle: (unit: SelectionUnit) => void;
}

function AddOnRow({ row, locked, basis, onToggle }: AddOnRowProps) {
    const { addOn } = row;
    return (
        <li className={`${GRID} py-2 text-sm ${row.tone === 'selected' ? 'bg-mp-primary/10' : ''}`}>
            <span className="col-start-1 row-start-1 flex justify-center">
                <Checkbox
                    ariaLabel={`Select ${addOn.name}`}
                    checked={row.checked}
                    disabled={locked || !row.selectable}
                    onChange={() => onToggle(row.unit)}
                />
            </span>
            <span className="col-start-2 row-start-1 min-w-0 break-words">
                <span title={addOn.description ?? undefined} className="block text-mp-foreground">{addOn.name}</span>
                {row.linkedTo && <span className="block text-xs text-mp-muted">Linked to {row.linkedTo}</span>}
            </span>
            <span className={`${QTY_CELL} text-mp-muted`}>
                <span className="@min-[28rem]:sr-only">Qty </span>
                {addOn.quantity}
            </span>
            <span className={`${DUE_CELL} text-mp-muted`}>
                <span className="@min-[28rem]:sr-only">Due </span>
                {row.dueDate ? <DueDateLabel date={row.dueDate} overdue={row.overdue} className="inline-flex text-sm" /> : '—'}
            </span>
            <span className={`${NET_CELL} tabular-nums text-mp-foreground`}>
                <span className="sr-only">{AMOUNT_BASIS_LABELS[basis].column} </span>
                {row.net}
            </span>
        </li>
    );
}

/** Every add-on still available to invoice, linked or not. Selectable in both modes. */
export function SoldAddOnsTable({ rows, emptyMessage, onToggle, locked = false, basis = 'net' }: Props) {
    return (
        <section aria-labelledby="sold-add-ons-heading" className="space-y-2">
            <h3 id="sold-add-ons-heading" className="text-sm font-semibold text-mp-foreground">
                Optional Extras
            </h3>
            {rows.length === 0 ? (
                <p className="text-sm text-mp-muted">{emptyMessage}</p>
            ) : (
                <div className="@container space-y-2">
                    <div aria-hidden="true" className={`${GRID} text-xs font-semibold uppercase tracking-wide text-mp-muted`}>
                        <span className="col-start-1 row-start-1" />
                        <span className="col-start-2 row-start-1">Name</span>
                        <span className={`${QTY_CELL} hidden @min-[28rem]:block`}>Qty</span>
                        <span className={`${DUE_CELL} hidden @min-[28rem]:block`}>Due</span>
                        <span className={NET_CELL}>{AMOUNT_BASIS_LABELS[basis].column}</span>
                    </div>
                    <ul className="divide-y divide-mp-border rounded border border-mp-border">
                        {rows.map((row) => (
                            <AddOnRow key={row.addOn.id} row={row} locked={locked} basis={basis} onToggle={onToggle} />
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
